import { z } from 'zod';
import { AcceptInvitationRequestSchema } from './api.js';
import { PLATFORM_CAPABILITIES, PlatformCapabilitySchema, type PlatformCapability } from './enums.js';

// «Plataforma → Equipo Estateflow»: who holds which platform capability. Only a platform super operator
// (isPlatformTeamAdmin: every admin capability) manages it. Platform-level, never workspace-scoped.

const Iso = z.string();
const Id = z.string().min(1).max(64);

/** Web route where a platform invitation is accepted (the emailed link carries `#token=…`). */
export const PLATFORM_INVITATION_PATH = '/invitacion-plataforma';

/** Full replacement list; duplicates are dropped and the order normalized (see normalizeCapabilities). */
const CapabilityList = z.array(PlatformCapabilitySchema).max(PLATFORM_CAPABILITIES.length * 2);

/** Deduplicated, in the canonical PLATFORM_CAPABILITIES order. */
export function normalizeCapabilities(list: readonly string[]): PlatformCapability[] {
  return PLATFORM_CAPABILITIES.filter((c) => list.includes(c));
}

export const PlatformTeamMemberSchema = z.object({
  userId: Id,
  name: z.string(),
  email: z.string(),
  capabilities: z.array(PlatformCapabilitySchema),
  /** Holds every admin capability (may manage this team). */
  superOperator: z.boolean(),
  /** The signed-in user (they cannot remove their own super-operator capabilities). */
  isSelf: z.boolean(),
  mfaEnabled: z.boolean(),
  disabledAt: Iso.nullable(),
  lastLoginAt: Iso.nullable(),
  /** Active MCP OAuth connections (ChatGPT/Claude); revoking platform_mcp closes them. */
  mcpConnections: z.number().int(),
});
export type PlatformTeamMember = z.infer<typeof PlatformTeamMemberSchema>;

export const PLATFORM_INVITATION_STATUSES = ['pending', 'expired'] as const;
export const PlatformInvitationSchema = z.object({
  id: Id,
  email: z.string(),
  capabilities: z.array(PlatformCapabilitySchema),
  /** Only open invitations are listed: pending, or expired and not yet replaced/revoked. */
  status: z.enum(PLATFORM_INVITATION_STATUSES),
  expiresAt: Iso,
  createdAt: Iso,
  invitedByName: z.string().nullable(),
});
export type PlatformInvitation = z.infer<typeof PlatformInvitationSchema>;

export const PlatformTeamSchema = z.object({
  members: z.array(PlatformTeamMemberSchema),
  invitations: z.array(PlatformInvitationSchema),
});
export type PlatformTeam = z.infer<typeof PlatformTeamSchema>;

export const SetPlatformCapabilitiesSchema = z.object({ capabilities: CapabilityList });

export const GrantPlatformAccessSchema = z.object({
  email: z.email('Escribe un correo válido').max(254),
  capabilities: CapabilityList.min(1, 'Elige al menos un permiso'),
});

/** An existing LIA account gets the capabilities right away (added to what it has); otherwise an invitation is emailed. */
export const GrantPlatformAccessResultSchema = z.discriminatedUnion('result', [
  z.object({ result: z.literal('granted'), member: PlatformTeamMemberSchema }),
  z.object({ result: z.literal('invited'), invitation: PlatformInvitationSchema }),
]);
export type GrantPlatformAccessResult = z.infer<typeof GrantPlatformAccessResultSchema>;

export const PlatformInvitationPreviewSchema = z.object({
  email: z.string(),
  capabilities: z.array(PlatformCapabilitySchema),
  expiresAt: Iso,
  invitedByName: z.string().nullable(),
  /** An account already exists for this email (the page asks to sign in instead of registering). */
  accountExists: z.boolean(),
});
export type PlatformInvitationPreview = z.infer<typeof PlatformInvitationPreviewSchema>;

export const AcceptPlatformInvitationRequestSchema = AcceptInvitationRequestSchema;
export const AcceptPlatformInvitationResponseSchema = z.object({ signedIn: z.boolean(), capabilities: z.array(PlatformCapabilitySchema) });
